import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Hammer, Clock } from 'lucide-react';
import { useAuth } from '../App';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions } from '../App'; // Import the functions instance from App.js

const PaymentIntegration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  // Stripe Price ID
  const PREMIUM_PLAN_PRICE_ID = 'price_1SeTp40Ta59pB9TnPoVsa8Qk';

  // Debug: Log auth state
  useEffect(() => {
    const auth = getAuth();
    console.log('=== AUTH DEBUG ===');
    console.log('useAuth currentUser:', currentUser);
    console.log('getAuth currentUser:', auth.currentUser);
    console.log('Auth state:', {
      uid: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified
    });
  }, [currentUser]);

  const handleSubscribe = async () => {
    console.log("=== SUBSCRIBE CLICKED ===");

    // Get auth instance
    const auth = getAuth();
    const user = auth.currentUser;

    console.log("Current user:", {
      exists: !!user,
      uid: user?.uid,
      email: user?.email,
      isAnonymous: user?.isAnonymous
    });

    if (!user) {
      setError('You must be logged in to subscribe. Please refresh the page and try again.');
      return;
    }

    // Check if user is anonymous
    if (user.isAnonymous) {
      setError('Anonymous users cannot subscribe. Please sign in with an email account.');
      return;
    }

    // Check if user has email
    if (!user.email) {
      setError('Your account must have an email address to subscribe.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Force refresh token to ensure it's valid
      console.log("Getting fresh token...");
      const token = await user.getIdToken(true);
      console.log("Token obtained:", token ? "✅ Yes" : "❌ No");

      // Use the functions instance from App.js (already initialized with the same auth)
      console.log("Using functions instance from App.js");

      // Verify token exists before calling function
      console.log("Auth object:", {
        currentUser: auth.currentUser ? "exists" : "null",
        uid: auth.currentUser?.uid,
        email: auth.currentUser?.email,
      });

      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      console.log("Calling createCheckoutSession function with:", {
        priceId: PREMIUM_PLAN_PRICE_ID,
        functionURL: functions.region,
      });

      const result = await createCheckoutSession({
        priceId: PREMIUM_PLAN_PRICE_ID,
      });

      console.log("✅ Function result:", result.data);
      const { sessionId, url } = result.data;

      // Direct redirect if URL provided
      if (url) {
        console.log("Redirecting to:", url);
        window.location.href = url;
        return;
      }

      // Otherwise use Stripe SDK
      if (sessionId) {
        console.log("Loading Stripe with session ID:", sessionId);
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(
          'pk_live_51SZufw0Ta59pB9Tndxk6Z8N696dSKHMA0iqp2V5M5jzimbNtktGty5PY2sZyzShFTGp0CTxjwP5te7QKsLLYSugq00ktLszOjf'
        );

        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: sessionId,
        });

        if (stripeError) {
          throw stripeError;
        }
      }

    } catch (err) {
      console.error('❌ Subscription error:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });

      let errorMessage = 'Failed to initiate checkout. Please try again.';

      if (err.code === 'unauthenticated') {
        errorMessage = 'Authentication failed. Please sign out and sign in again.';
      } else if (err.code === 'failed-precondition') {
        errorMessage = 'You are already subscribed to Premium.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen p-6 pt-24"
      style={{
        background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)',
      }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <button
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors mb-4"
            onClick={() => window.history.back()}
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">
            Upgrade to Premium
          </h1>
          <p className="text-gray-300">
            Unlock unlimited features and find your perfect match
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
          {loading ? (
            <div className="py-12">
              <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>
                Processing Payment...
              </h2>
              <p className="text-xl text-gray-600">
                Redirecting you to secure checkout...
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8 relative inline-block">
                <div
                  className="w-32 h-32 rounded-full flex items-center justify-center mx-auto"
                  style={{
                    background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)',
                  }}
                >
                  <Hammer size={64} className="text-white" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Clock size={24} style={{ color: 'hsl(43, 74%, 52%)' }} />
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-4xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  Choose Your Plan
                </h2>
                <p className="text-xl text-gray-600">
                  Unlock unlimited features with our Premium plan.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8 mb-8 max-w-2xl mx-auto">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  Premium Benefits
                </h3>
                <div className="grid md:grid-cols-2 gap-3 text-left">
                  {[
                    'Unlimited profile views',
                    'Unlimited shortlist',
                    'Unlimited interests',
                    'Priority visibility',
                    'Advanced filters',
                    '24/7 Premium support',
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ background: 'hsl(43, 74%, 52%)' }}
                      ></div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 font-medium">{error}</p>
                </div>
              )}

              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="px-8 py-4 rounded-full text-white font-bold text-lg transition-all transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  background: loading
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)',
                }}
              >
                Choose Premium
              </button>
            </>
          )}
        </div>

        <div className="mt-8 text-center text-gray-300 text-sm">
          <p className="flex items-center justify-center gap-2 mb-2">
            <Shield size={16} />
            Secure Stripe payment
          </p>
          <p>Payments are encrypted and PCI compliant</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentIntegration;